import { BUILD_STEPS_COMPONENT_BY_TYPE } from './BuildSteps';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import SaveIcon from '@mui/icons-material/Save';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  ButtonProps,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import { BoardID, RANDOM_BOARD_ID } from 'BoardID';
import { generateRandomBoardHash } from 'RandomBoardGenerator';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';

import {
  useCreateNewBoard,
  useDeleteBoard,
  useSaveBoard,
} from 'state/recoilBoardActions';
import {
  isNewModalOpenAtom,
  selectAreChangesPending,
  selectedBoardIDAtom,
  selectIsLocalBoard,
  settingsAtom,
  workingBoardInstanceAtomFamily,
} from 'state/recoilState';
import { useSelectedBoardInstance } from 'utils/hooks';
import useRouter from 'utils/useRouter';

function ButtonBar() {
  const settings = useRecoilValue(settingsAtom);
  const selectedBoardID = useRecoilValue(selectedBoardIDAtom);
  const selectedBoardInstance = useSelectedBoardInstance();
  const router = useRouter();

  const setIsNewModalOpen = useSetRecoilState(isNewModalOpenAtom);

  const createNewBoardAction = useCreateNewBoard();
  const saveBoardAction = useSaveBoard();
  const deleteBoardAction = useDeleteBoard();

  const theme = useTheme();

  const isCustomBoard = useRecoilValue(selectIsLocalBoard(selectedBoardID));
  const areChangesPending = useRecoilValue(
    selectAreChangesPending(selectedBoardID)
  );

  const duplicate = () => {
    if (selectedBoardInstance == null) {
      return;
    }

    const newBoard = selectedBoardInstance.duplicate();
    createNewBoardAction(newBoard);

    router.navToBoardID(newBoard.id as BoardID);
  };

  const createRandom = () => {
    router.navToBoardHash(generateRandomBoardHash(settings));
  };

  const revert = useRecoilCallback(
    ({ reset }) =>
      () => {
        if (selectedBoardInstance == null) {
          return;
        }

        reset(workingBoardInstanceAtomFamily(selectedBoardID));
      },
    [selectedBoardID, selectedBoardInstance]
  );

  const save = () => {
    if (selectedBoardInstance == null) {
      return;
    }

    saveBoardAction(selectedBoardInstance);
  };

  const deleteBoard = () => {
    deleteBoardAction(selectedBoardID);
  };

  const openBuildInstructions = () => {
    if (selectedBoardID === RANDOM_BOARD_ID) {
      selectedBoardInstance &&
        router.navToInstructionsForHash(selectedBoardInstance.getHash());
    } else {
      router.navToInstructionsForID(selectedBoardID);
    }
  };

  const supportsBuildSteps =
    selectedBoardInstance &&
    BUILD_STEPS_COMPONENT_BY_TYPE[selectedBoardInstance.type];

  return (
    <Box
      sx={{
        pb: 2,
        mb: 1,
        position: 'sticky',
        top: 0,
        zIndex: 2,
        display: 'flex',
        justifyContent: 'space-between',
        background: theme.palette.background.default,
      }}
    >
      <Grid container spacing={1} wrap="nowrap">
        <Grid item>
          <ButtonBarButton
            variant="contained"
            color="primary"
            onClick={() => setIsNewModalOpen(true)}
            tooltip="Create a new board"
            startIcon={<AddIcon />}
            label="New"
          />
        </Grid>

        <Grid item>
          <ButtonBarButton
            variant="outlined"
            color="primary"
            onClick={createRandom}
            tooltip="Generate a random board"
            startIcon={<ShuffleIcon />}
            label="Random"
          />
        </Grid>
        <Grid item>
          <ButtonBarButton
            variant="outlined"
            color="primary"
            onClick={duplicate}
            tooltip="Save a copy"
            startIcon={<LibraryAddIcon />}
            label="Save a copy"
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} justifyContent="flex-end" wrap="nowrap">
        {isCustomBoard && (
          <>
            <Grid item>
              <ButtonBarButton
                variant="contained"
                color="primary"
                disabled={!areChangesPending}
                onClick={save}
                tooltip="Save board"
                startIcon={<SaveIcon />}
                label="Save"
              ></ButtonBarButton>
            </Grid>
            <Grid item>
              <ButtonBarButton
                variant="outlined"
                color="primary"
                disabled={!areChangesPending}
                onClick={revert}
                tooltip="Revert board"
                startIcon={<UndoIcon />}
                label="Revert"
              ></ButtonBarButton>
            </Grid>
            <Grid item>
              <ButtonBarButton
                variant="contained"
                color="error"
                onClick={deleteBoard}
                tooltip="Delete board"
                startIcon={<DeleteIcon />}
                label="Delete"
              ></ButtonBarButton>
            </Grid>
            <Grid item>
              <Divider
                orientation="vertical"
                style={{ margin: `0 ${theme.spacing(1)}px` }}
              />
            </Grid>
          </>
        )}
        <Grid item>
          <ButtonBarButton
            color="primary"
            disabled={!supportsBuildSteps}
            onClick={openBuildInstructions}
            tooltip={
              supportsBuildSteps
                ? ''
                : `Build instructions are not yet available for ${
                    selectedBoardInstance?.type ?? ''
                  } boards`
            }
            startIcon={<BuildIcon />}
            label="Build steps"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function ButtonBarButton(
  props: Pick<
    ButtonProps,
    'color' | 'disabled' | 'onClick' | 'startIcon' | 'variant'
  > & {
    label: string;
    tooltip: string;
  }
) {
  const { label, tooltip, ...buttonProps } = props;
  const { startIcon, ...iconButtonProps } = buttonProps;

  return (
    <Tooltip title={tooltip || label}>
      <div>
        <Button
          aria-label={label}
          {...buttonProps}
          style={{ whiteSpace: 'nowrap' }}
          sx={{ display: { lg: 'flex', xs: 'none' } }}
        >
          {label}
        </Button>

        <Box sx={{ display: { lg: 'none', xs: 'inline-flex' } }}>
          <IconButton aria-label={label} {...iconButtonProps}>
            {startIcon}
          </IconButton>
        </Box>
      </div>
    </Tooltip>
  );
}

export default ButtonBar;
